import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Volume2, VolumeX, X } from "lucide-react";
import { scrollToId } from "./scroll";
import { startAmbience, stopAmbience } from "./ambience";
import { SteamIcon, STEAM_URL } from "./SteamIcon";

const LINKS = [
  ["INTEL", "intel"],
  ["ARSENAL", "arsenal"],
  ["OPERATIONS", "operations"],
  ["TRANSMISSION", "transmission"],
];

const WINDOW_CLOSE = new Date("2026-11-07T04:00:00Z").getTime();

const useCountdown = () => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, WINDOW_CLOSE - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000) % 24;
  const m = Math.floor(diff / 60000) % 60;
  const s = Math.floor(diff / 1000) % 60;
  return `${String(d).padStart(3, "0")}:${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export const HudNav = () => {
  const [coords, setCoords] = useState("00.0000N / 00.0000E");
  const [sound, setSound] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const countdown = useCountdown();

  useEffect(() => {
    if (menuOpen) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
  }, [menuOpen]);

  useEffect(() => {
    const tick = () =>
      setCoords(`${(Math.random() * 90).toFixed(4)}N / ${(Math.random() * 180).toFixed(4)}E`);
    tick();
    const t = setInterval(tick, 2400);
    return () => clearInterval(t);
  }, []);

  const toggleSound = () => {
    if (sound) stopAmbience();
    else startAmbience();
    setSound(!sound);
  };

  const goTo = (id) => {
    setMenuOpen(false);
    window.__lenis?.start();
    document.body.style.overflow = "";
    requestAnimationFrame(() => scrollToId(id));
  };

  return (
    <>
      <header
      data-testid="hud-nav"
      className="fixed inset-x-0 top-0 z-50 border-b border-line bg-[#0A0E12]/90 backdrop-blur-md"
    >
      <div className="flex h-14 items-center justify-between gap-4 px-4 md:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToId("hero")}
          className="flex items-center gap-3 font-mono text-xs font-bold tracking-widest"
        >
          <span className="flex h-6 w-6 items-center justify-center border border-signal text-signal">
            E
          </span>
          <span className="hidden text-[#F8FAFC] sm:inline">
            PROJECT ECLIPSE <span className="text-signal">// PROTOCOL ZERO</span>
          </span>
        </button>
        <div
          data-testid="nav-coordinates"
          className="hidden font-mono text-[10px] tracking-widest text-slate-500 xl:block"
        >
          SAT-UPLINK: <span className="text-signal">LOCKED</span> // GRID {coords}
        </div>
        <div
          data-testid="mission-countdown"
          className="hidden font-mono text-[10px] tracking-[0.25em] text-slate-500 md:block"
        >
          MISSION WINDOW T-MINUS <span className="text-signal">{countdown}</span>
        </div>
        <nav className="flex items-center gap-6">
          {LINKS.map(([label, id]) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => scrollToId(id)}
              className="hidden font-mono text-[11px] tracking-widest text-slate-400 transition-colors hover:text-signal lg:block"
            >
              {label}
            </button>
          ))}
          <button
            data-testid="ambience-toggle-btn"
            onClick={toggleSound}
            aria-label="Toggle audio ambience"
            className="flex h-8 w-8 items-center justify-center border border-line text-slate-400 transition-colors hover:border-signal hover:text-signal"
          >
            {sound ? (
              <Volume2 className="h-3.5 w-3.5 text-signal" />
            ) : (
              <VolumeX className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMenuOpen(true)}
            aria-label="Open dossier menu"
            className="flex h-8 w-8 items-center justify-center border border-line text-slate-400 transition-colors hover:border-signal hover:text-signal lg:hidden"
          >
            <Menu className="h-3.5 w-3.5" />
          </button>
          <a
            data-testid="nav-wishlist-btn"
            href={STEAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-pulse clip-angle-sm hidden items-center gap-2 border border-line px-4 py-2 font-mono text-[10px] font-bold tracking-widest text-slate-300 transition-colors hover:border-signal hover:text-signal sm:flex"
          >
            <SteamIcon className="h-3.5 w-3.5" />
            <span className="hidden xl:inline">WISHLIST ON STEAM</span>
          </a>
          <button
            data-testid="nav-request-access-btn"
            onClick={() => scrollToId("access")}
            className="clip-angle-sm border border-signal px-4 py-2 font-mono text-[10px] font-bold tracking-widest text-signal transition-colors hover:bg-signal hover:text-[#0A0E12]"
          >
            REQUEST ACCESS
          </button>
        </nav>
      </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              data-testid="mobile-menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
              onClick={() => setMenuOpen(false)}
            />
            <motion.aside
              data-testid="mobile-menu-panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="fixed inset-y-0 right-0 z-[70] flex w-72 flex-col border-l border-signal/40 bg-[#0A0E12]"
            >
              <div className="flex h-14 items-center justify-between border-b border-line px-5">
                <span className="font-mono text-[10px] font-bold tracking-[0.3em] text-signal">
                  SITE INDEX // CLASSIFIED
                </span>
                <button
                  data-testid="mobile-menu-close-btn"
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close dossier menu"
                  className="flex h-8 w-8 items-center justify-center border border-line text-slate-400 transition-colors hover:border-signal hover:text-signal"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <nav className="flex-1">
                {[
                  ["01", "INTEL", "intel"],
                  ["02", "ARSENAL", "arsenal"],
                  ["03", "OPERATIONS", "operations"],
                  ["04", "REQUEST ACCESS", "access"],
                  ["05", "TRANSMISSION", "transmission"],
                ].map(([num, label, id]) => (
                  <button
                    key={id}
                    data-testid={`mobile-nav-link-${id}`}
                    onClick={() => goTo(id)}
                    className="group flex w-full items-baseline gap-4 border-b border-line px-5 py-5 text-left transition-colors hover:bg-[#121820]"
                  >
                    <span className="font-mono text-[10px] tracking-[0.3em] text-slate-600">
                      {num}
                    </span>
                    <span
                      className={`font-mono text-lg font-bold tracking-[0.2em] transition-colors ${
                        id === "access"
                          ? "text-signal"
                          : "text-[#F8FAFC] group-hover:text-signal"
                      }`}
                    >
                      {label}
                    </span>
                  </button>
                ))}
              </nav>
              <a
                data-testid="mobile-wishlist-btn"
                href={STEAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mx-5 mb-4 flex items-center justify-center gap-2 border border-line px-4 py-3 font-mono text-[10px] font-bold tracking-widest text-slate-300 transition-colors hover:border-signal hover:text-signal"
              >
                <SteamIcon className="h-3.5 w-3.5" />
                WISHLIST ON STEAM
              </a>
              <div className="space-y-2 border-t border-line px-5 py-5 font-mono text-[10px] tracking-[0.25em] text-slate-600">
                <p>
                  T-MINUS <span className="text-signal">{countdown}</span>
                </p>
                <p>
                  GRID <span className="text-slate-400">{coords}</span>
                </p>
                <p className="text-slate-700">ALL TRANSMISSIONS MONITORED</p>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
